import { AirportOperationalStatus } from "../../models/airports/AirportConfig";

interface AirportWithOperationalStatus {
  isPinned?: boolean;
  otp?: {
    overall?: {
      status?: AirportOperationalStatus;
    };
  };
}

export interface AirportStatusCount {
  good: number;
  moderate: number;
  poor: number;
  isPinned: number;
  all: number;
}

export function formatMinutesToHoursMinutes(
  minutes: number | null | undefined
): string {
  const roundedMinutes =
    typeof minutes === "number" && Number.isFinite(minutes)
      ? Math.max(0, Math.round(minutes))
      : 0;
  const hours = Math.floor(roundedMinutes / 60)
    .toString()
    .padStart(2, "0");
  const remainingMinutes = (roundedMinutes % 60).toString().padStart(2, "0");

  return `${hours}:${remainingMinutes}`;
}

export const getOverallOtpStatus = (airport: AirportWithOperationalStatus) =>
  airport.otp?.overall?.status;

export function findMatches(wordToMatch: string | RegExp, airportList: any[]) {
  return airportList.filter((airport: { iataCode: string; name: string }) => {
    const regex = new RegExp(wordToMatch, "gi");
    return airport.iataCode.match(regex) || airport.name.match(regex);
  });
}

export function filterAirportsByStatus<T extends AirportWithOperationalStatus>(
  airportList: T[],
  status: string | undefined
): T[] {
  if (status === "all") {
    return airportList;
  } else if (status === "pinned") {
    return airportList.filter((airport) => airport.isPinned);
  } else {
    return airportList.filter(
      (airport) => getOverallOtpStatus(airport) === status
    );
  }
}

export function calculateAirportStatusCount(
  airportList: AirportWithOperationalStatus[]
) {
  return airportList.reduce<AirportStatusCount>(
    (acc, airport) => {
      acc.all += 1;

      const overallStatus = getOverallOtpStatus(airport);
      if (overallStatus === "good") {
        acc.good += 1;
      } else if (overallStatus === "moderate") {
        acc.moderate += 1;
      } else if (overallStatus === "poor") {
        acc.poor += 1;
      }

      if (airport.isPinned) {
        acc.isPinned += 1;
      }
      return acc;
    },
    {
      good: 0,
      moderate: 0,
      poor: 0,
      isPinned: 0,
      all: 0,
    }
  );
}

export function getCommonAirports(
  searchData: any[] = [],
  filteredData: any[] = []
) {
  const searchDataIds = searchData.map((airport: any) => airport.iataCode);
  const filteredDataIds = filteredData.map((airport: any) => airport.iataCode);
  const commonIds = searchDataIds.filter((id: string) =>
    filteredDataIds.includes(id)
  );
  return filteredData.filter((airport: any) =>
    commonIds.includes(airport.iataCode)
  );
}

export function splitAirportDataByPin(airportList: any[] = []) {
  const { pinnedAirports, unPinnedAirports } = airportList.reduce(
    (acc: { pinnedAirports: any[]; unPinnedAirports: any[] }, airport: any) => {
      if (airport.isPinned) {
        acc.pinnedAirports.push(airport);
      } else {
        acc.unPinnedAirports.push(airport);
      }
      return acc;
    },
    { pinnedAirports: [], unPinnedAirports: [] }
  );
  return { pinnedAirports, unPinnedAirports };
}

export function extractAirportMapDetails(airportList: any[]) {
  return airportList.map((airport: any) => {
    return {
      id: airport.iataCode,
      code: airport.iataCode,
      name: airport.name,
      location: airport.location,
    };
  });
}
